
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface UseVideoUploadProps {
  onUploadComplete: (videoUrl: string, videoTitle: string) => void;
  onClose: () => void;
}

export const useVideoUpload = ({ onUploadComplete, onClose }: UseVideoUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [videoTitle, setVideoTitle] = useState("");
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setError(null);
    
    if (file) {
      // Validate file type
      if (!file.type.includes('video/')) {
        setError("Please select a valid video file");
        return;
      }
      
      // Validate file size (limit to 100MB)
      if (file.size > 100 * 1024 * 1024) {
        setError("Video file size should be less than 100MB");
        return;
      }
      
      setVideoFile(file);
      
      // Auto-generate a title from the filename if none exists
      if (!videoTitle) {
        const nameWithoutExtension = file.name.split('.').slice(0, -1).join('.');
        setVideoTitle(nameWithoutExtension);
      }
    }
  };

  const handleUpload = async () => {
    if (!videoFile) {
      setError("Please select a video to upload");
      return;
    }
    
    if (!videoTitle.trim()) {
      setError("Please enter a title for the video");
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    setError(null);
    
    try {
      // Use the file name as a unique identifier
      const fileName = `${Date.now()}-${videoFile.name}`;
      
      // Custom progress tracking
      let progressInterval: ReturnType<typeof setInterval>;
      progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          // Simulate progress since Supabase doesn't provide real-time progress
          // Cap at 90% until we get confirmation of completion
          const newProgress = prev + (Math.random() * 5);
          return Math.min(newProgress, 90);
        });
      }, 500);
      
      // Upload the file to Supabase Storage
      const { data, error: uploadError } = await supabase
        .storage
        .from('videos')
        .upload(fileName, videoFile, {
          cacheControl: '3600',
          upsert: false
        });
      
      clearInterval(progressInterval);
      
      if (uploadError) throw uploadError;
      
      setUploadProgress(100);
      
      // Get the public URL for the uploaded video
      const { data: urlData } = supabase
        .storage
        .from('videos')
        .getPublicUrl(fileName);
      
      toast({
        title: "Upload successful",
        description: "Your video has been uploaded successfully.",
      });
      
      onUploadComplete(urlData.publicUrl, videoTitle);
      onClose();
      
      // Reset the form
      resetForm();
      
    } catch (error: any) {
      console.error("Error uploading video:", error);
      setError(error.message || "An error occurred during upload. Please try again.");
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: error.message || "An error occurred during upload. Please try again.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setVideoFile(null);
    setVideoTitle("");
    setError(null);
    setUploadProgress(0);
  };

  return {
    videoTitle,
    setVideoTitle,
    videoFile,
    isUploading,
    uploadProgress,
    error,
    handleFileChange,
    handleUpload,
    resetForm
  };
};
